import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import FamilyManagementSummaryPageFilterSection from './components/FamilyManagementSummaryPageFilterSection';
import { Form, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { useGetUserListQuery } from '@/api/user-management/userManagementListApiSlice';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { FamilyFilter, FamilyPageDTO } from '@/api/DTO/family/family.interface';
import { useGetFamilyListQuery } from '@/api/family-management/familyApi';
import NewFamilyMemberButton from './components/NewFamilyMemberButton';
import { PermissionEnum } from '@/api/enum/common.enum';
import { useGetUser } from '@/hooks/useGetUser';

export default function FamilyManagementSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const { userInfo } = useGetUser();

	const defaultPropertyFilter: FamilyFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		membershipRoleList: [],
		statusList: [],
	};
	const defaultValues: DefaultValues<FamilyFilter> = defaultPropertyFilter;
	const methods = useForm<FamilyFilter>({
		defaultValues,
	});
	const transformedSearchParams: FamilyFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'membershipRoleList',
			'statusList',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFamilyListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: FamilyPageDTO[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				// lastLogin: !item.lastLogin
				// 	? '-'
				// 	: dayjs(item.lastLogin).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as FamilyFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'familyMemberFileNo',
			headerName: t('family.familyMemberFileNo'),
			flex: 20,
		},
		{
			field: 'fileNo',
			headerName: t('family.fileNo'),
			flex: 20,
		},
		{
			field: 'nameEn',
			headerName: t('family.name'),
			flex: 20,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'phone',
			headerName: t('family.mobilePhone'),
			flex: 20,
		},
		{
			field: 'membershipRole',
			headerName: t('family.membershipRole'),
			flex: 20,
		},
		{
			field: 'familyMemberStatus',
			headerName: t('family.status'),
			flex: 20,
			renderCell: (params) => (
				<Chip
					color={
						params.row.familyMemberStatus == 1 ? 'default' : 'chipTertiary'
					}
					label={params.row.familyMemberStatusDesc}
				/>
			),
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) =>
				userInfo?.permissionList?.includes(PermissionEnum.ViewFamilyMember) && (
					<Link
						to={`/family-management/family-management-list/detail/${params.id}`}
						state={{
							propertySummarySearchParams: qs.stringify(
								transformedSearchParams
							),
						}}
					>
						<IconButton color='secondary'>
							<VisibilityOutlinedIcon />
						</IconButton>
					</Link>
				),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.familyMemberSummary')}
				</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					{userInfo?.permissionList?.includes(
						PermissionEnum.CreateFamilyMember
					) && <NewFamilyMemberButton />}
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<FamilyManagementSummaryPageFilterSection></FamilyManagementSummaryPageFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
